import { Injectable } from '@angular/core';
import { ChangeStatusMessage } from '@core/definitions/api/chat.definitions';
import { ChatService } from '@core/services/chat.service';
import { filterArrayContainsValue, sortArrayOfObjects } from '@core/utils/array.util';
import { ToastController } from '@ionic/angular';
import type { Color } from '@ionic/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { Conversation, MESSAGE_STATUS, Search, User } from '../chat.definitions';
import * as actions from './chat.actions';
import { ChatSelectConversationAction, ChatSendMessagesHasRecivedAction, ChatUpdateConversationAction, ChatUpdateUnreadMessagesCountAction } from './chat.actions';
export const defaultState: ChatModel = {
  showModule: false,
  currentUser: null,
  search: { size: 10, cursors: {} },
  filters: {},
  sort: { lastSend: -1 },
  conversations: [],
  filterConversations: [],
  selectedConversation: null,
  unreadMessages: {},
  loading: true,
  moduleOpen: false,
};
export interface ChatModel {
  showModule: boolean;
  currentUser: User;
  search: Search,
  filters: any;
  sort: { [key: string]: any };
  conversations: Conversation[];
  filterConversations: Conversation[];
  unreadMessages: { [key: string]: number };
  selectedConversation: Conversation;
  loading: boolean;
  moduleOpen: boolean
}
@State<ChatModel>({
  name: 'chat',
  defaults: defaultState,
})
@Injectable({ providedIn: 'root' })
export class ChatState {
  constructor(
    private _chat: ChatService,
    private _toast: ToastController,
  ) {
  }
  @Selector()
  static currentUser(state: ChatModel) {
    return state.currentUser;
  }
  @Selector()
  static isOpen(state: ChatModel) {
    return state.moduleOpen;
  }
  @Selector()
  static loading(state: ChatModel) {
    return state.loading;
  }
  @Selector()
  static selectedConversation(state: ChatModel) {
    return state.selectedConversation;
  }
  @Selector()
  static conversations(state: ChatModel) {
    return state.conversations;
  }
  @Selector()
  static filterConversations(state: ChatModel) {
    return state.filterConversations;
  }
  @Selector()
  static totalUnReadedMessages(state: ChatModel) {
    return Object.keys(state.unreadMessages).reduce((a, b) => a + state.unreadMessages[b], 0);
  }
  @Selector()
  static unReadedMessages(state: ChatModel) {
    return state.unreadMessages;
  }
  @Selector()
  static search(state: ChatModel) {
    return state.search;
  }
  @Action(actions.ChatSetCurrentUserAction)
  chatSetCurrentUser({ patchState }: StateContext<ChatModel>, { user }: actions.ChatSetCurrentUserAction) {
    patchState({ currentUser: user });
  }
  @Action(actions.ChatSetModuleOpenAction, { cancelUncompleted: true })
  chatSetModuleOpenAction({ patchState }: StateContext<ChatModel>, { isOpen }: actions.ChatSetModuleOpenAction) {
    patchState({ moduleOpen: isOpen });
  }
  @Action(actions.ChatSetLoadingModuleAction, { cancelUncompleted: true })
  chatSetLoadingModuleAction({ patchState }: StateContext<ChatModel>, { loading }: actions.ChatSetLoadingModuleAction) {
    patchState({ loading });
  }
  @Action(actions.ChatFilterConversationsAction, { cancelUncompleted: true })
  chatFilterConversationsAction({ setState, getState }: StateContext<ChatModel>, { filter, value }: actions.ChatFilterConversationsAction) {
    const state: ChatModel = getState();
    const filters = JSON.parse(JSON.stringify(getState().filters));
    filters[filter] = value;
    setState({ ...getState(), filters: { ...filters } });
    let temp: Conversation[] = state.conversations.slice();
    Object.keys(filters).map(k => {
      const valueFilter: any = filters[k];
      if (valueFilter || valueFilter === false) {
        switch (k) {
          case 'search':
            temp = filterArrayContainsValue(temp, valueFilter, ['name']);
            setState({ ...state, filterConversations: temp.slice() });
            break;
          case 'messagesLength':
            temp = temp.filter(e => e.messages.length > valueFilter);
            setState({
              ...state,
              filterConversations: sortArrayOfObjects(temp.slice(), 'lastSend', false).slice(),
            });
        }
      } else {
        setState({
          ...state,
          filterConversations: state.conversations,
        });
      }
    });
  }
  @Action(actions.ChatUpdateConversationAction, { cancelUncompleted: true })
  chatUpdateConversation({ setState, getState, dispatch }: StateContext<ChatModel>, { conversation }: actions.ChatUpdateConversationAction) {
    const converationToUpdate = JSON.parse(JSON.stringify(conversation));
    converationToUpdate.lastSend = converationToUpdate.messages[converationToUpdate.messages.length - 1];
    setState(patch({ conversations: updateItem<Conversation>(c => c.id === converationToUpdate.id, converationToUpdate) }));
    setState(patch({ filterConversations: updateItem<Conversation>(c => c.id === converationToUpdate.id, converationToUpdate) }));
    let conversations = getState().conversations.slice();
    let filterConversations = getState().filterConversations.slice();
    conversations = this.sortConversationsByLastSend(conversations);
    filterConversations = this.sortConversationsByLastSend(filterConversations);
    setState({ ...getState(), conversations: conversations, filterConversations: filterConversations });
  }
  @Action(actions.ChatSelectConversationAction, { cancelUncompleted: true })
  chatSelectConversation({ patchState }: StateContext<ChatModel>, { conversation }: actions.ChatSelectConversationAction) {
    patchState({ selectedConversation: conversation });
  }
  // Recive
  @Action(actions.ChatReciveCreateNewConversationAction, { cancelUncompleted: true })
  async chatReciveCreateNewConversation({ dispatch }: StateContext<ChatModel>, { conversation }: actions.ChatReciveCreateNewConversationAction) {
    dispatch(new ChatSendMessagesHasRecivedAction());
    conversation.messages[0].status = MESSAGE_STATUS.RECIVED;
    conversation.lastSend.status = MESSAGE_STATUS.RECIVED;
    dispatch(new ChatUpdateConversationAction(conversation));
    dispatch(new ChatUpdateUnreadMessagesCountAction([{ conversation: conversation.id, count: 1 }]));
  }
  @Action(actions.ChatReciveNewMessageAction, { cancelUncompleted: true })
  chatReciveNewMessage({ getState, dispatch }: StateContext<ChatModel>, { message }: actions.ChatReciveNewMessageAction) {
    const conversations: Conversation[] = JSON.parse(JSON.stringify(getState().conversations));
    let conversation: Conversation = conversations.find((conversation: Conversation) => conversation.id === message.conversation);
    conversation.messages.push({ ...message.message, status: MESSAGE_STATUS.RECIVED });
    conversation.lastSend = { ...message.message, status: MESSAGE_STATUS.RECIVED };
    dispatch(new ChatUpdateConversationAction(conversation));
    if (!getState().selectedConversation || getState().selectedConversation.id !== conversation.id) {
      dispatch(new ChatUpdateUnreadMessagesCountAction([{ conversation: conversation.id, count: 1 }]));
    } else {
      dispatch(new ChatSelectConversationAction(conversation));
    }
    this.showNotification(getState(), 'Nuevo mensaje', 'success', message.message.body.slice(0, 20), new Date());
    dispatch(new ChatSendMessagesHasRecivedAction());
  }
  @Action(actions.ChatReciveMessagesHasRecivedAction, { cancelUncompleted: true })
  chatReciveMessagesHasRecived({ getState, dispatch }: StateContext<ChatModel>, { changeStatus }: actions.ChatReciveMessagesHasRecivedAction) {
    const conversations = JSON.parse(JSON.stringify(getState().conversations));
    let conversation: Conversation = conversations.find((conversation: Conversation) => conversation.id === Object.keys(changeStatus)[0]);
    conversation.messages = conversation.messages.map(message => {
      if (message?.from?.user === getState().currentUser?.user && message.status === MESSAGE_STATUS.SEND) {
        return { ...message, status: MESSAGE_STATUS.RECIVED };
      }
      return message;
    });
    if (conversation.lastSend?.from?.user === getState().currentUser?.user && conversation.lastSend.status === MESSAGE_STATUS.SEND) {
      conversation.lastSend.status = MESSAGE_STATUS.RECIVED;
    }
    if (getState().selectedConversation && getState().selectedConversation.id === conversation.id) {
      dispatch(new ChatSelectConversationAction(conversation));
    }
    dispatch(new ChatUpdateConversationAction(conversation));
  }
  @Action(actions.ChatReciveMessagesHasReadedAction, { cancelUncompleted: true })
  chatReciveMessagesHasReaded({ getState, dispatch }: StateContext<ChatModel>, { changeStatus }: actions.ChatReciveMessagesHasReadedAction) {
    const conversations = JSON.parse(JSON.stringify(getState().conversations));
    let conversation: Conversation = conversations.find((conversation: Conversation) => conversation.id === Object.keys(changeStatus)[0]);
    conversation.messages = conversation.messages.map(message => {
      if (message.from?.user === getState().currentUser?.user && message.status === MESSAGE_STATUS.RECIVED) {
        return { ...message, status: MESSAGE_STATUS.READ };
      }
      return message;
    });
    if (conversation.lastSend.from?.user === getState().currentUser?.user && conversation.lastSend.status === MESSAGE_STATUS.RECIVED) {
      conversation.lastSend.status = MESSAGE_STATUS.READ;
    }
    if (getState().selectedConversation && getState().selectedConversation.id === conversation.id) {
      dispatch(new ChatSelectConversationAction(conversation));
    }
    dispatch(new ChatUpdateConversationAction(conversation));
  }
  @Action(actions.ChatSetConversationsAction, { cancelUncompleted: true })
  chatSetConversations({ patchState, getState, dispatch }: StateContext<ChatModel>, { response }: actions.ChatSetConversationsAction) {
    const search: { size: number, cursors: { [key: string]: any } } = { size: 10, cursors: {} };
    const conversations = response.conversations.map((conversation: Conversation) => {
      if (conversation.lastSend) {
        search.cursors[conversation.id] = conversation.messages[0].date;
      }
      return conversation;
    });
    patchState({ filters: {}, search, filterConversations: conversations, conversations });
    dispatch(new ChatSendMessagesHasRecivedAction());
  }
  @Action(actions.ChatSendMessagesHasRecivedAction, { cancelUncompleted: true })
  async chatSendMessagesHasRecived({ getState }: StateContext<ChatModel>): Promise<boolean> {
    const conversations = getState().conversations.slice();
    const conversationsToSend: ChangeStatusMessage = {};
    conversations.forEach(c => conversationsToSend[c.id] = c.messages.filter(m => m.status === MESSAGE_STATUS.SEND && m.from?.user !== getState().currentUser?.user).map(m => m.id));
    return await this._chat.sendMessagesHasRecived(conversationsToSend);
  }
  @Action(actions.ChatSendMessagesHasReadedAction, { cancelUncompleted: true })
  async chatSendMessagesHasReaded({ setState, getState, dispatch }: StateContext<ChatModel>, { ids }: actions.ChatSendMessagesHasReadedAction) {
    let conversations: Conversation[] = getState().conversations.slice();
    let conversationsReaded: Conversation[] = conversations.slice().filter(c => ids.some(id => id === c.id) && c.messages.length > 0);
    if (conversationsReaded.length === 0) {
      return;
    }
    await this._chat.sendMessagesHasReaded({ conversations: conversationsReaded.map(c => c.id) });
    dispatch(new ChatUpdateUnreadMessagesCountAction(conversationsReaded.map(c => {
      return { conversation: c.id, count: 0 };
    })));
    const updated = conversations.map(c1 => {
      const i = conversationsReaded.findIndex(c2 => c2.id === c1.id);
      if (i != -1) {
        const messages = c1.messages.map(m => {
          if (m.status === MESSAGE_STATUS.RECIVED && m.from?.user !== getState().currentUser?.user) {
            return { ...m, status: MESSAGE_STATUS.READ };
          }
          return m;
        });
        return { ...c1, messages: messages };
      }
      return c1;
    }).slice();
    setState({ ...getState(), filterConversations: updated, conversations: updated });
  }
  @Action(actions.ChatGetMessagesUnreadCountAction, { cancelUncompleted: true })
  async chatGetMessagesUnreadCount({ setState, getState }: StateContext<ChatModel>, { unreadMessagesCount }: actions.ChatGetMessagesUnreadCountAction) {
    setState(patch({ unreadMessages: unreadMessagesCount }));
  }
  @Action(actions.ChatUpdateUnreadMessagesCountAction, { cancelUncompleted: true })
  async chatUpdateUnreadMessagesCount({ setState, getState }: StateContext<ChatModel>, { payload }: actions.ChatUpdateUnreadMessagesCountAction) {
    const unreadMessages: { [key: string]: number } = JSON.parse(JSON.stringify(getState().unreadMessages));
    payload.forEach(conversation => {
      if (conversation.count === 0) {
        unreadMessages[conversation.conversation] = conversation.count;
      } else {
        unreadMessages[conversation.conversation] = unreadMessages[conversation.conversation] + conversation.count;
      }
    });
    setState(patch({ unreadMessages: unreadMessages }));
  }
  @Action(actions.ChatGetMessagesAction, { cancelUncompleted: true })
  async chatGetMessages({ dispatch, getState, setState }: StateContext<ChatModel>, { payload }: actions.ChatGetMessagesAction) {
    const response = await this._chat.getMessages(payload);
    if (response.messages.length === 0) {
      return null;
    }
    const search: Search = JSON.parse(JSON.stringify(getState().search));
    search.cursors[response.conversation] = response.messages[0].date;
    setState(patch({ search: search }));
    dispatch(new ChatSendMessagesHasRecivedAction());
    return response.messages;
  }
  private sortConversationsByLastSend(conversations: Conversation[]): Conversation[] {
    return conversations.sort((a, b) => {
      if (a.lastSend && b.lastSend) {
        return new Date(a.lastSend.date).getTime() > new Date(b.lastSend.date).getTime() ? -1 : new Date(a.lastSend.date).getTime() < new Date(b.lastSend.date).getTime() ? 1 : 0;
      }
      return 1;
    });
  }
  private async showNotification(state: ChatModel, header: string, color: Color, message?: string, time?: Date): Promise<void> {
    if (!state.moduleOpen) {
      const headerText = time ? `${header} ${new Date(time).getHours()}:${new Date(time).getMinutes()}` : header;
      const messageText = message ? message.length > 35 ? message.substring(0, 35) + '...' : message : null;
      const toast = await this._toast.create({
        animated: true,
        header: headerText,
        message: messageText,
        color: color,
        duration: 5000,
      });
      await toast.present();
    }
  }
}
